import React, { useState, useEffect, useRef, useCallback } from 'react';
import Image from '../Image';
import classes from './index.module.scss';

/**
 * Props interface for the Tag component
 */
interface Props {
	/** Initial text content of the tag */
	initialTitle: string;
	/** Callback function when the tag value changes */
	onValueChanged?: ({ prev, cur }: { prev: string; cur: string }) => any;
	/** Callback function when the tag is removed */
	onRemove?: (value: string) => any;
	/** Whether the tag should be focused on mount */
	focused?: boolean;
	/** Whether the tag is editable */
	editable?: boolean;
}

/**
 * Tag component
 * Renders an editable tag with text content
 * Supports editing, removing, and focusing
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} - Rendered component or null if removed
 */
const Tag = ({ initialTitle = '', onValueChanged, focused, onRemove, editable = true }: Props) => {
	// Current tag title
	const [title, setTitle] = useState(initialTitle);
	// Temporary title used during editing
	const [tempTitle, setTempTitle] = useState(initialTitle);
	// Whether the tag should be removed
	const [remove, setRemove] = useState(false);
	// Reference to the input element for focusing and selection
	const inputRef = useRef<HTMLInputElement>(null);

	/**
	 * Select all text in the input field
	 * Only works if the tag is editable and the input is available
	 */
	const selectText = () => {
		if (!inputRef.current || !editable) {
			return;
		}
		inputRef.current.select();
	};

	/**
	 * Focus and select text when the focused prop changes
	 */
	useEffect(() => {
		if (inputRef.current && focused) {
			inputRef.current.select();
		}
	}, [focused]);

	/**
	 * Call onRemove callback when the tag is marked for removal
	 */
	useEffect(() => {
		if (remove && onRemove) {
			onRemove(title);
		}
	}, [remove, title, onRemove]);

	/**
	 * Handle keyboard events in the input field
	 * Supports Escape to cancel editing and Enter to confirm changes
	 *
	 * @param {React.KeyboardEvent<HTMLInputElement>} e - Keyboard event
	 */
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			// Cancel editing on Escape key
			if (e.key === 'Escape') {
				inputRef.current?.blur();
			}
			// Confirm changes on Enter key
			if (e.key === 'Enter') {
				inputRef.current?.blur();
				if (tempTitle.trim().length) {
					// Update title and notify parent component
					setTitle((prev) => {
						onValueChanged && onValueChanged({ prev, cur: tempTitle });
						return tempTitle;
					});
				} else {
					// Remove tag if title is empty
					setRemove(true);
				}
			}
		},
		[tempTitle, setTitle, onValueChanged]
	);

	/**
	 * Handle blur event on the input field
	 * Removes the tag if title is empty, otherwise updates the temporary title
	 */
	const handleBlur = useCallback(() => {
		if (title.length <= 0) {
			setRemove(true);
		} else {
			setTempTitle(title);
		}
	}, [title, setTempTitle]);

	/**
	 * Handle remove button click
	 * Marks the tag for removal
	 */
	const handleRemove = useCallback(() => {
		setRemove(true);
	}, []);

	return (
		<>
			{/* Only render the tag if it's not marked for removal */}
			{!remove && (
				<div className={classes['tag']}>
					{/* Editable input field for the tag title */}
					<input
						// Dynamic width based on content length
						style={{ width: title.length * 8 + 'px' }}
						ref={inputRef}
						onClick={() => selectText()}
						onFocus={() => selectText()}
						onKeyDown={handleKeyDown}
						onBlur={handleBlur}
						className={classes['tag-title']}
						value={tempTitle}
						onChange={(e) => editable && setTempTitle(e.target.value)}
						disabled={!editable}
					/>
					{/* Close button - only shown if the tag is editable */}
					{editable && (
						<span onClick={() => handleRemove()} className={classes['tag-close']}>
							<Image src="icClose" />
						</span>
					)}
				</div>
			)}
		</>
	);
};

export default Tag;
